import React from 'react';
import {createStyles, makeStyles, Theme} from '@material-ui/core/styles';
import Typography from '@material-ui/core/Typography';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import {getShipmentDetails} from "../../api/ShipmentEndpoint";
import {RR_STATUS} from "../../constants/RRConstants";
import {useSnackbar} from "notistack";
import {useTranslation} from "react-i18next";
import Loader from "../core/Loader";
import Grid from '@material-ui/core/Grid';
import withWidth, {WithWidthProps} from "@material-ui/core/withWidth";
import {Accordion, AccordionDetails, AccordionSummary, ListItem, ListItemText, Paper, Toolbar} from "@material-ui/core";
import {InfoCardLine} from "../page-single-shipment-tracking/ShipmentInfoCard";
import List from "@material-ui/core/List";
import {TFunction} from 'i18next';


interface IField {
    key: string
    value: string
    keyParams: string[]
    valueParams: string[]
    translateable: boolean
}

interface IMobileShipmentDetailsOverviewDTO {
    fields: IField[]
}

interface IMobileTransportOrderContainer {
    fields: IField[]
}

interface IMobileDimentionsDTO {
    peaces: number
    packageType: string
    weight: number
    volume: number
}

interface IMobileDangerousGoodDTO {
    unNumber: string
    classification: string
    packagingGroup: string
}


interface IShipmentDetails {
    uuid: string
    modality: string
    category: string
    overview: IMobileShipmentDetailsOverviewDTO
    containers: IMobileTransportOrderContainer[]
    cargos: IMobileDimentionsDTO[]
    cargoTotal: IMobileDimentionsDTO
    dangerousGoods: IMobileDangerousGoodDTO[]
}


const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        root: {
            flex: 1,
            "& .MuiExpansionPanelDetails-root": {
                padding: 5,
                backgroundColor: "#fafafa"
            },
            "& .MuiPaper-root": {
                height: " 100%",
            }
        },
        title: {
            display: "flex",
            alignItems: "center",
            "& img": {
                height: 24,
                width: 24,
                marginRight: 10
            },
            "& .MuiSvgIcon-root": {
                marginRight: 10
            }
        },
        link: {
            display: "flex",
            alignItems: "center",
            "& a": {
                textDecoration: "underline",
                color: theme.palette.primary.main,
                fontSize: 15
            }
        },
        paper: {
            padding: 15
        },
        titleBar: {
            color: "#fff",
            backgroundColor: theme.palette.primary.main
        },
    }),
);

const ShipmentDetailsExpansionPanel: React.FC<{ uuid: string } & WithWidthProps> = ({uuid, width}) => {
    const classes = useStyles();
    const {t} = useTranslation();

    return (
        <div className={classes.root}>
            <Accordion TransitionProps={{unmountOnExit: true}}>
                <AccordionSummary
                    expandIcon={<ExpandMoreIcon/>}
                    aria-controls="details-content"
                    id="details-header"
                >
                    <div className={classes.title}>
                        <img src={"/assets/icons/box_closed.png"} alt={"parcel"}/>
                        <Typography variant="button">{t("shipment.details")}</Typography>
                    </div>
                </AccordionSummary>
                <AccordionDetails >
                    <DetailsGrid uuid={uuid} width={width}/>
                </AccordionDetails >
            </Accordion>
        </div>
    );
};


const DetailsGrid: React.FC<{ uuid: string } & WithWidthProps> = ({uuid, width}) => {

    const {enqueueSnackbar} = useSnackbar();
    const {t} = useTranslation();
    const classes = useStyles();


    const [shipmentDetails, setShipmentDetails] = React.useState<IShipmentDetails | undefined>(undefined);

    React.useEffect(() => {

        getShipmentDetails(uuid).then(({data}) => {
            const {status, response} = data;

            if (status !== RR_STATUS.OK) {
                enqueueSnackbar(t("core.server-error"), {
                    variant: "error",
                    preventDuplicate: false
                });
            } else {
                setShipmentDetails(response);
            }
        })
    }, [enqueueSnackbar, t, setShipmentDetails, uuid]);

    if (!shipmentDetails) {
        return <div className={classes.root}>
            <Loader/>
        </div>;
    }

    return <Grid
        container
        direction="row"
        spacing={1}
    >

        <Grid item xs={12} sm={6}>
            <ShipmentDetailsList shipmentDetails={shipmentDetails} width={width}/>
        </Grid>
        <Grid item xs={12} sm={6}>
            <ContainersList containers={shipmentDetails?.containers} width={width}/>
        </Grid>
    </Grid>
};

function translateValue(t: TFunction, value: string) {
    if (value.includes(",")) {
        return value.split(",").map(s => translateValue(t, s)).join(",");
    }
    return t("fields.tracking-shipment-overview-area-" + value.replace(".", "-"));
}

function getListInfoCard(fields: IField[], t: TFunction, width: "xs" | "sm" | "md" | "lg" | "xl" | undefined) {
    return <div>
        {fields.map((field, index) => {

            const caption = field.keyParams && field.keyParams.length > 1 ? t("fields.tracking-shipment-overview-area-" + field.key, {
                    weight: field.keyParams[0],
                    vol: field.keyParams[1]
                })
                : t("fields.tracking-shipment-overview-area-" + field.key);
            let value = field.translateable && field.value ? translateValue(t, field.value) : field.value;

            if (field.valueParams && field.valueParams.length > 0) {
                value = [value, field.valueParams].join(" ");
            }

            return <InfoCardLine key={index}
                                 property={caption}
                                 value={value}
                                 width={width}/>
        })}


    </div>;
}

const ContainersList: React.FC<{ containers: IMobileTransportOrderContainer[] } & WithWidthProps> = ({containers, width}) => {
    const {t} = useTranslation();
    const classes = useStyles();

    if (containers.length === 0) {
        return <></>;
    }

    return (
        <div>
            <Toolbar variant="dense" className={classes.titleBar}>
                <Typography variant="h6">{t("fields.tracking-shipment-overview-area-container-header")}</Typography>
            </Toolbar>
            <Grid container spacing={1}>
                {containers.map((container, indexGrid) => {
                    return <Grid item xs={12} key={indexGrid}>
                        <Paper className={classes.paper}>
                            {getListInfoCard(container.fields, t, width)}
                        </Paper>
                    </Grid>
                })}
            </Grid>
        </div>
    )

};

const ShipmentDetailsList: React.FC<{ shipmentDetails: IShipmentDetails } & WithWidthProps> = ({shipmentDetails, width}) => {
    const {t} = useTranslation();

    const {overview} = shipmentDetails;


    return <Paper>
        <List>
            <ListItem>
                <ListItemText
                    primary={
                        getListInfoCard(overview.fields, t, width)
                    }
                />
            </ListItem>

        </List></Paper>
};


export default withWidth()(ShipmentDetailsExpansionPanel);